'use client';
import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { CheckCircle, XCircle, Ban, Eye } from 'lucide-react';
import { toast } from 'sonner';

const INITIAL_FLAGS = [
{ id: 'fp-001', dogName: 'Diamond', breed: 'Toy Poodle', owner: 'unknown@tempmail.io', reason: 'Suspected fake/spam profile', reporter: 'community-report', reportedAt: 'Jun 21, 2026', aiScore: 87, severity: 'high', photo: "https://images.unsplash.com/photo-1660590622457-df46156432d3", photoAlt: 'Small dog on profile' },
{ id: 'fp-002', dogName: 'Rocky', breed: 'German Shepherd', owner: 'j.morrison@email.com', reason: 'Inappropriate cover photo', reporter: 'system-ai', reportedAt: 'Jun 20, 2026', aiScore: 72, severity: 'medium', photo: "https://img.rocket.new/generatedImages/rocket_gen_img_1a4b16626-1768037138038.png", photoAlt: 'German shepherd Rocky' },
{ id: 'fp-003', dogName: 'Princess', breed: 'Chihuahua', owner: 'lisa.parker@gmail.com', reason: 'Misleading breed information', reporter: 'user-report', reportedAt: 'Jun 19, 2026', aiScore: 34, severity: 'low', photo: "https://images.unsplash.com/photo-1693615774599-ea266ebf51f4", photoAlt: 'Small dog profile photo' }];


const SEVERITY_STYLES: Record<string, string> = {
  high: 'bg-red-50 text-danger border-red-200',
  medium: 'bg-orange-50 text-warning border-orange-200',
  low: 'bg-yellow-50 text-yellow-700 border-yellow-200'
};

export default function FlaggedProfilesTable() {
  const [flags, setFlags] = useState(INITIAL_FLAGS);
  const [confirmAction, setConfirmAction] = useState<{id: string;action: string;} | null>(null);
  const [selected, setSelected] = useState<string[]>([]);

  const toggleSelect = (id: string) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSelected((s) => s.length === flags.length ? [] : flags.map((f) => f.id));

  const handleAction = (id: string, action: string) => {
    setFlags((f) => f.filter((flag) => flag.id !== id));
    setConfirmAction(null);
    const labels: Record<string, string> = { approve: 'Profile approved and restored.', reject: 'Profile rejected and removed.', suspend: 'User account suspended.', restore: 'Profile restored successfully.' };
    toast.success(labels[action] || 'Action completed.');
  };

  const handleBulkAction = (action: string) => {
    setFlags((f) => f.filter((flag) => !selected.includes(flag.id)));
    toast.success(`${selected.length} profiles ${action === 'approve' ? 'approved' : 'removed'}.`);
    setSelected([]);
  };

  return (
    <>
      {/* Bulk bar */}
      {selected.length > 0 &&
      <div className="flex items-center gap-3 mb-4 p-3 bg-primary/10 border border-primary/20 rounded-xl fade-in">
          <span className="text-sm font-700 text-primary">{selected.length} selected</span>
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={() => handleBulkAction('approve')} className="paw-btn flex items-center gap-1.5 px-3 py-1.5 bg-success text-white rounded-lg text-xs font-700">
              <CheckCircle size={13} />Approve All
            </button>
            <button onClick={() => handleBulkAction('remove')} className="paw-btn flex items-center gap-1.5 px-3 py-1.5 bg-danger text-white rounded-lg text-xs font-700">
              <XCircle size={13} />Remove All
            </button>
          </div>
        </div>
      }

      <div className="bg-card border border-border rounded-2xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/40">
                <th className="px-4 py-3 w-10">
                  <input type="checkbox" checked={selected.length === flags.length && flags.length > 0} onChange={toggleAll} className="rounded border-border" />
                </th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Dog Profile</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Flag Reason</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Reporter</th>
                <th className="text-left px-4 py-3 text-xs font-600 text-muted-foreground">Reported</th>
                <th className="text-center px-4 py-3 text-xs font-600 text-muted-foreground">AI Score</th>
                <th className="text-center px-4 py-3 text-xs font-600 text-muted-foreground">Severity</th>
                <th className="text-center px-4 py-3 text-xs font-600 text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {flags.map((flag) =>
              <tr key={flag.id} className={`hover:bg-muted/30 transition-colors ${selected.includes(flag.id) ? 'bg-primary/5' : ''}`}>
                  <td className="px-4 py-4">
                    <input type="checkbox" checked={selected.includes(flag.id)} onChange={() => toggleSelect(flag.id)} className="rounded border-border" />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg overflow-hidden bg-muted shrink-0">
                        <AppImage src={flag.photo} alt={flag.photoAlt} width={36} height={36} className="object-cover w-full h-full" />
                      </div>
                      <div>
                        <p className="font-700 text-sm text-foreground">{flag.dogName}</p>
                        <p className="text-xs text-muted-foreground">{flag.breed}</p>
                        <p className="text-xs text-muted-foreground">{flag.owner}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 max-w-[180px]">
                    <p className="text-sm text-foreground line-clamp-2">{flag.reason}</p>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-xs text-muted-foreground">{flag.reporter}</span>
                  </td>
                  <td className="px-4 py-4">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{flag.reportedAt}</span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`text-sm font-800 stat-badge ${flag.aiScore >= 70 ? 'text-danger' : flag.aiScore >= 40 ? 'text-warning' : 'text-success'}`}>
                      {flag.aiScore}%
                    </span>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-600 border ${SEVERITY_STYLES[flag.severity]}`}>
                      {flag.severity}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-center gap-1">
                      <button
                      onClick={() => setConfirmAction({ id: flag.id, action: 'approve' })}
                      className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center text-success hover:bg-green-100 transition-colors"
                      title="Approve profile — mark as safe and restore">
                      
                        <CheckCircle size={13} />
                      </button>
                      <button
                      onClick={() => setConfirmAction({ id: flag.id, action: 'reject' })}
                      className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center text-danger hover:bg-red-100 transition-colors"
                      title="Reject and remove profile">
                      
                        <XCircle size={13} />
                      </button>
                      <button
                      onClick={() => setConfirmAction({ id: flag.id, action: 'suspend' })}
                      className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center text-warning hover:bg-orange-100 transition-colors"
                      title="Suspend user account — prevents further activity">
                      
                        <Ban size={13} />
                      </button>
                      <button
                      className="w-7 h-7 rounded-lg bg-muted flex items-center justify-center text-muted-foreground hover:text-primary hover:bg-secondary transition-colors"
                      title="View full profile">
                      
                        <Eye size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {flags.length === 0 &&
        <div className="flex flex-col items-center justify-center py-16 text-center">
            <CheckCircle size={40} className="text-success mb-3" />
            <p className="font-700 text-base text-foreground">No flagged profiles</p>
            <p className="text-sm text-muted-foreground mt-1">All profile reports have been reviewed.</p>
          </div>
        }
      </div>

      <ConfirmModal
        open={!!confirmAction}
        title={
        confirmAction?.action === 'approve' ? 'Approve Profile' :
        confirmAction?.action === 'reject' ? 'Reject & Remove Profile' : 'Suspend User Account'
        }
        description={
        confirmAction?.action === 'approve' ? 'This will mark the profile as safe and dismiss the flag.' :
        confirmAction?.action === 'reject' ? 'This will permanently remove the profile and notify the user.' :
        'This will suspend the user account and prevent all activity. The user will be notified.'
        }
        confirmLabel={
        confirmAction?.action === 'approve' ? 'Approve' :
        confirmAction?.action === 'reject' ? 'Remove Profile' : 'Suspend Account'
        }
        destructive={confirmAction?.action !== 'approve'}
        onConfirm={() => confirmAction && handleAction(confirmAction.id, confirmAction.action)}
        onCancel={() => setConfirmAction(null)} />
      
    </>);

}